import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { Product } from '@bytel/bytel-sales';
import { PartnerError, UPDATE_ACTIONS } from '@bytel/checkout';

import { FaiService } from '@services/fai.services';
import { MainCartService } from '@services/main-cart.service';
import { MessageService } from '@services/message.service';
import { PartnersService } from '@services/partners.service';
import { TagGeneratorServices } from '@services/tag-generator.services';

import { Observable, of } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';

import { ByouErrorModalComponent } from '../shared/byou-error-modal/byou-error-modal.component';
import { ErrorModalComponent } from '../shared/error-modal/error-modal.component';
import { PartnerExclusivityErrorModalComponent } from '../shared/partner-exclusivity-error-modal/partner-exclusivity-error-modal.component';
import { TagService } from '../shared/tag/tag.service';

// @dynamic
@Injectable({
    providedIn: 'root',
})
export class QuoteResolver {
    constructor(
        private messageService: MessageService,
        private partnersService: PartnersService,
        private tagService: TagService,
        private checkoutTagsServices: TagGeneratorServices,
        private faiService: FaiService,
        private cartService: MainCartService,
    ) {}

    public resolve(route: ActivatedRouteSnapshot): Observable<Product | Product[] | boolean> {
        if (!route.fragment || route.fragment.includes('access_token')) {
            // Auto add FAI auto-add=GENCODE
            if (route.queryParams?.['auto-add']) {
                const gencodeFai: string = route.queryParams?.['auto-add'];
                const isPro: boolean = !!route.queryParams?.isPro;

                return this.faiService.addFai(gencodeFai, isPro).pipe(
                    tap(() => this.faiService.save()),
                    catchError(() => {
                        this.messageService.openModal(ErrorModalComponent);
                        return of(false);
                    }),
                    mergeMap(() => of(true)),
                );
            }
            this.faiService.save();

            return of(true);
        }

        const fragements: string[] = route.fragment.split('/').filter((value: string) => !!value);

        if (fragements.length < 2) {
            return of(false);
        }

        const action: string = fragements[0];
        const urlKeys: string[] = fragements[fragements.length - 1].replace('pack-', '').split('_');

        if (!urlKeys.length) {
            return of(false);
        }

        if (action === UPDATE_ACTIONS.ADD) {
            this.cartService.reset();
            this.cartService.addQuote();

            const partner = this.partnersService.isPartnerOfferValid(urlKeys);
            if (partner) {
                this.messageService.openModal(PartnerExclusivityErrorModalComponent, {
                    partner: partner.partner,
                });
                this._sendTag();
                return of(false);
            }
        }
        return this.cartService.addProductsByAttributes(urlKeys, 'url_key').pipe(
            catchError((err) => {
                if (err instanceof PartnerError) {
                    this._showYamaErrorModal();
                } else {
                    this._showErrorModal(urlKeys?.some((el) => el.includes('b-you')));
                }

                return of(false);
            }),
        );
    }

    private _showErrorModal(isSowo: boolean = false): void {
        if (isSowo) {
            this.messageService.openModal(ByouErrorModalComponent);
        } else {
            this.messageService.openModal(ErrorModalComponent);
        }
    }

    private _showYamaErrorModal(): void {
        this.messageService.openModal(PartnerExclusivityErrorModalComponent, {
            partner: 'votre espace bancaire',
        });
    }

    private _sendTag(): void {
        this.tagService.send(
            this.checkoutTagsServices.getTags('Blocage_operation_exclusive', 'panier', {
                pageCategorie: 'Information',
                activiteB2BouB2C: 'B2C',
            }),
        );
    }
}
